import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@babackapp/customer-ui/lib/utils";

const alertVariants = cva(
  "group/alert relative grid w-full gap-1.5 rounded-lg border px-3 py-4 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-3 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "bg-destructive text-destructive-foreground *:data-[slot=alert-description]:text-destructive-foreground/90 *:[svg]:text-current border-destructive-border",
        pending:
          "bg-pending text-pending-foreground *:data-[slot=alert-description]:text-pending-foreground/90 *:[svg]:text-current border-pending-border",
        info: "bg-info text-info-foreground *:data-[slot=alert-description]:text-info-foreground/90 *:[svg]:text-current border-info-border",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-medium font-sans group-has-[>svg]/alert:col-start-2 text-base [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "max-h-[26vh] overflow-y-auto overscroll-contain text-sm compact:text-xs font-content text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4 [&_ol]:list-decimal [&_ul]:list-disc [&_:is(ol,ul)]:pl-5 [&_:is(ol,ul):not(:last-child)]:mb-4 [&_li]:my-1",
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2 right-2", className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
